import {
	FILE_SIZE_2MB_BYTE,
	FILE_SIZE_20MB_BYTE,
	PageTypeEnum,
} from '@/constant';
import { TGetDocumentDetailResponse } from '@/services';
import {
	CommonActions,
	DefineDocumentAreaActions,
	GlobalDispatch,
} from '@/stores';

import { t } from 'i18next';
import { isNilOrEmpty } from 'ramda-adjunct';

import { showAlert } from './alert.util';
import { NOOP } from './common.util';
import { isBase64, onDownloadFileFromServer } from './file.util';
export const hasRealContent = (html: string) => {
	const cleaned = html
		.replace(/<br\s*\/?>/gi, '')
		.replace(/&nbsp;|&#160;/g, ' ')
		.replace(/<[^>]*>/g, '') // xóa text trong thẻ
		.trim();

	const hasMediaTag = /<(img|video|iframe|audio)\b[^>]*>/i.test(html);

	return !!cleaned || hasMediaTag;
};

export const checkContentSize = (
	content: string,
	size = FILE_SIZE_2MB_BYTE
) => {
	const encoder = new TextEncoder();
	const sizeInBytes = encoder.encode(content).length;
	// console.log(sizeInBytes / 1024 / 1024, 'mb');
	return sizeInBytes > size;
};

export const getImageIdsFromContentEditor = (content: string) => {
	if (isNilOrEmpty(content)) return [];

	const parser = new DOMParser();
	const doc = parser.parseFromString(content, 'text/html');
	const ids = Array.from(doc.querySelectorAll('img[data-id]'))
		.map((img) => img.getAttribute('data-id'))
		.filter((id): id is string => Boolean(id));

	return ids;
};

export const prepareContentBeforeSave = async (
	value: string,
	base64UrlToFileMap?: Map<string, File>,
	options?: {
		initialImageIds?: string[];
		initialContent?: string;
		documentType?: string;
		refTableName?: PageTypeEnum;
		refTableId?: string;
		maxTotalContentSize?: number;
		maxTotalImageSize?: number;
	}
): Promise<{
	isValid: boolean;
	content: string;
	deletedImageIds: string[];
	uploadedImageIds: number[];
}> => {
	const {
		initialImageIds,
		initialContent,
		documentType,
		refTableName,
		refTableId,
		maxTotalContentSize,
		maxTotalImageSize = FILE_SIZE_20MB_BYTE,
	} = options || {};

	const parser = new DOMParser();
	const doc = parser.parseFromString(value, 'text/html');
	const initDoc = initialContent
		? parser.parseFromString(initialContent, 'text/html')
		: null;

	if (doc.querySelector('parsererror')) {
		console.error('Invalid HTML content provided to prepareContentBeforeSave');
		return {
			isValid: false,
			content: value,
			deletedImageIds: [],
			uploadedImageIds: [],
		};
	}

	const imgElements = doc.querySelectorAll('img');
	const idsStillExist: string[] = [];
	const uploadedImageIds: number[] = [];
	const uploadPromises: Promise<void>[] = [];

	const cloneDoc = doc.cloneNode(true) as Document;
	cloneDoc.querySelectorAll('img').forEach((img) => img.remove());

	const cleanedContent = (cloneDoc as any).body?.innerHTML || '';

	if (checkContentSize(cleanedContent, maxTotalContentSize)) {
		showAlert(t('message:ALERT_8'), NOOP);
		return {
			isValid: false,
			content: value,
			deletedImageIds: [],
			uploadedImageIds: [],
		};
	}

	const totalSize = Array.from(imgElements).reduce((acc, img) => {
		const sizeStr = img.getAttribute('data-size');
		const size = sizeStr ? parseInt(sizeStr, 10) : 0;
		return acc + (isNaN(size) ? 0 : size);
	}, 0);

	if (totalSize > maxTotalImageSize) {
		showAlert(t('common:cannot-save-because-storage-limit-exceeded'), NOOP);
		return {
			isValid: false,
			content: value,
			deletedImageIds: [],
			uploadedImageIds: [],
		};
	}

	imgElements.forEach((img) => {
		const src = img.getAttribute('src');
		const dataId = img.getAttribute('data-id');

		if (dataId && src?.startsWith('data:') && initDoc) {
			const originalImg = Array.from(
				initDoc.querySelectorAll('img[data-id]')
			).find((img) => img.getAttribute('data-id') === dataId);

			const originalSrc = originalImg?.getAttribute('src');
			if (originalSrc) {
				img.setAttribute('src', originalSrc);
			}

			idsStillExist.push(dataId);
			return;
		}

		if (dataId) {
			idsStillExist.push(dataId);
			return;
		}

		if (src && base64UrlToFileMap?.has(src)) {
			const mutipartFile = base64UrlToFileMap.get(src)!;

			const uploadPromise = new Promise<void>((resolve, reject) => {
				const reqUploadDocumentList = [
					{
						fileType: 'UPLOAD',
						mutipartFile,
						documentType,
					},
				];

				GlobalDispatch(
					CommonActions.commonFileUploadRequest({
						data: {
							reqUploadDocumentList,
							refTableName,
							refTableId,
						},
						callback: ({
							data,
							error,
						}: {
							data?: TGetDocumentDetailResponse[];
							error: any;
						}) => {
							const newData = data?.[0];
							if (error || !newData?.id) {
								console.warn('Upload error or missing document ID');
								reject(error);
							} else {
								img.setAttribute('data-id', newData.id.toString());
								img.setAttribute('data-name', newData.name);
								img.setAttribute('data-file-id', newData.fileId);
								img.setAttribute('src', newData.path);

								uploadedImageIds.push(newData.id);
								resolve();
							}
						},
					})
				);
			});

			uploadPromises.push(uploadPromise);
		}
	});

	await Promise.all(uploadPromises);

	const newContent = doc.body.innerHTML;
	const deletedImageIds =
		initialImageIds?.filter((id) => !idsStillExist.includes(id)) || [];

	if (deletedImageIds.length > 0) {
		GlobalDispatch(
			DefineDocumentAreaActions.deleteDocumentsRequest({
				params: {
					refTableName,
					refTableId,
					history: false, //avatar, companySeal has value true
					id: deletedImageIds,
				},
			})
		);
	}

	return {
		isValid: true,
		content: newContent,
		deletedImageIds,
		uploadedImageIds,
	};
};

export const prepareContentWithBase64Images = async (
	content: string
): Promise<string> => {
	if (!content) return '';

	const parser = new DOMParser();
	const doc = parser.parseFromString(content, 'text/html');

	if (doc.querySelector('parsererror')) {
		console.error(
			'Invalid HTML content provided to prepareContentWithBase64Images'
		);
		return content;
	}

	const imgElements = doc.querySelectorAll('img');
	const promises: Promise<void>[] = [];

	imgElements.forEach((img) => {
		const path = img.getAttribute('src');

		if (path) {
			const p = onDownloadFileFromServer(path, undefined, {
				output: 'base64',
				prefixPath: 'file/download/',
			})
				.then((base64: any) => {
					if (isBase64(base64)) {
						img.setAttribute('src', base64);
						return;
					}

					throw new Error('Invalid base64');
				})
				.catch((error) => {
					console.warn(
						'Failed to fetch or convert image with path:',
						path,
						error
					);
				});

			promises.push(p);
		}
	});

	await Promise.all(promises);

	return doc.body.innerHTML;
};

export const convertHTMLToDeltaFromEditor = (editor?: any, html?: string) => {
	if (!editor || !html) return html;
	return editor.clipboard.convert(html);
};
